import React, { useState, useEffect } from 'react';
import { Settings, Save, Check, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppConfig } from '../hooks/useAppConfig';
import { SECTION_META, SECTION_ORDER, AppConfigRow, AppConfigSection } from '../services/appConfig';

export function ConfigPage() {
  const { rows, loading, savingKey, error, save } = useAppConfig();

  if (loading) {
    return (
      <div style={{ padding: '24px' }}>
        <div className="skeleton-list">
          {[1, 2, 3, 4, 5].map(i => <div key={i} className="skeleton-row" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <div className="card" style={{ display: 'flex', gap: '12px', alignItems: 'center', color: 'var(--red)' }}>
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  const grouped: Record<AppConfigSection, AppConfigRow[]> = {
    validation: [],
    performance: [],
    bar_interface: [],
  };
  for (const r of rows) {
    if (grouped[r.section]) grouped[r.section].push(r);
  }

  return (
    <div style={{ padding: '24px', maxWidth: '900px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
        <Settings size={20} />
        <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>Configuration</h1>
      </div>
      <p className="muted" style={{ marginBottom: '24px', fontSize: '13px' }}>
        Paramètres dynamiques de l'app. Lus au démarrage par l'app mobile, applicables sans rebuild.
      </p>

      {SECTION_ORDER.map(section => {
        const sectionRows = grouped[section];
        if (sectionRows.length === 0) return null;
        const meta = SECTION_META[section];
        return (
          <div key={section} style={{ marginBottom: '32px' }}>
            <div style={{ marginBottom: '12px' }}>
              <h2 style={{ fontSize: '15px', fontWeight: 700, marginBottom: '2px' }}>{meta.title}</h2>
              <p className="muted" style={{ fontSize: '12px' }}>{meta.subtitle}</p>
            </div>
            <div className="card" style={{ padding: 0 }}>
              {sectionRows.map((row, idx) => (
                <ConfigRowEditor
                  key={row.key}
                  row={row}
                  saving={savingKey === row.key}
                  onSave={value => handleSave(row, value)}
                  separator={idx < sectionRows.length - 1}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );

  async function handleSave(row: AppConfigRow, newValue: number | boolean | string) {
    try {
      await toast.promise(save(row.key, newValue), {
        loading: 'Sauvegarde…',
        success: `${row.label} mis à jour`,
        error: 'Erreur — vérifie les permissions',
      });
    } catch {
      // toast.promise affiche déjà l'erreur
    }
  }
}

interface RowProps {
  row: AppConfigRow;
  saving: boolean;
  separator: boolean;
  onSave: (value: number | boolean | string) => void;
}

function ConfigRowEditor({ row, saving, separator, onSave }: RowProps) {
  const [draft, setDraft] = useState<number | boolean | string>(row.value);

  // Sync draft when external value changes (RT update)
  useEffect(() => {
    setDraft(row.value);
  }, [row.value]);

  const dirty = draft !== row.value;

  const updatedAt = row.updated_at ? new Date(row.updated_at) : null;
  const updatedLabel = updatedAt
    ? `Maj ${updatedAt.toLocaleDateString('fr-FR')} ${updatedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}${row.updated_by ? ` · ${row.updated_by}` : ''}`
    : null;

  return (
    <div
      style={{
        padding: '14px 18px',
        borderBottom: separator ? '1px solid var(--border)' : 'none',
        display: 'flex',
        gap: '16px',
        alignItems: 'flex-start',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '2px' }}>{row.label}</div>
        {row.description && (
          <div className="muted" style={{ fontSize: '12px', lineHeight: 1.4 }}>{row.description}</div>
        )}
        {updatedLabel && (
          <div style={{ fontSize: '11px', color: 'var(--text-dim)', marginTop: '4px' }}>{updatedLabel}</div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
        {row.value_type === 'boolean' ? (
          <ToggleField
            value={draft as boolean}
            onChange={v => setDraft(v)}
            disabled={saving}
          />
        ) : row.value_type === 'number' ? (
          <input
            type="number"
            value={String(draft)}
            onChange={e => setDraft(parseFloat(e.target.value) || 0)}
            disabled={saving}
            style={{
              width: '90px',
              padding: '6px 10px',
              fontSize: '13px',
              fontWeight: 600,
              textAlign: 'right',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              color: 'var(--text-primary)',
              outline: 'none',
            }}
          />
        ) : (
          <input
            type="text"
            value={String(draft)}
            onChange={e => setDraft(e.target.value)}
            disabled={saving}
            style={{
              width: '160px',
              padding: '6px 10px',
              fontSize: '13px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              color: 'var(--text-primary)',
              outline: 'none',
            }}
          />
        )}
        <button
          className={dirty ? 'btn btn-primary' : 'btn btn-outline'}
          disabled={!dirty || saving}
          onClick={() => onSave(draft)}
          style={{ fontSize: '12px', padding: '6px 12px', minWidth: '80px', justifyContent: 'center' }}
        >
          {saving ? <span className="spinner-sm" /> : dirty ? <><Save size={12} /> Save</> : <><Check size={12} /> OK</>}
        </button>
      </div>
    </div>
  );
}

function ToggleField({ value, onChange, disabled }: { value: boolean; onChange: (v: boolean) => void; disabled: boolean }) {
  return (
    <button
      onClick={() => !disabled && onChange(!value)}
      disabled={disabled}
      style={{
        width: '44px',
        height: '24px',
        borderRadius: '14px',
        border: 'none',
        background: value ? 'var(--green)' : 'var(--border)',
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background 0.15s',
        padding: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '2px',
          left: value ? '22px' : '2px',
          width: '20px',
          height: '20px',
          borderRadius: '10px',
          background: '#fff',
          transition: 'left 0.15s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }}
      />
    </button>
  );
}
